import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import {
  Users, Trophy, Target, Calendar, Zap, MapPin, Music,
  Globe, MessageCircle,
  UserPlus, UserCheck, Heart, ArrowLeft,
} from "lucide-react";
import { FanLevelBadge } from "@/components/FanLevelBadge";
import { BadgesSection } from "@/components/BadgesSection";
import { SOCIAL_PLATFORMS } from "@/config/socialPlatforms";

export default function UserProfilePage() {
  const { userId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [profileUser, setProfileUser] = useState(null);
  const [stats, setStats] = useState({});
  const [level, setLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followStatus, setFollowStatus] = useState({ i_follow: false, they_follow: false, is_friend: false });
  const [followLoading, setFollowLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const [profileRes, followRes] = await Promise.all([
        api.get(`/auth/user/${userId}`),
        userId !== user?.id ? api.get(`/user-follows/${userId}/status`).catch(() => ({ data: { i_follow: false, they_follow: false, is_friend: false } })) : Promise.resolve({ data: { i_follow: false, they_follow: false, is_friend: false } }),
      ]);
      setProfileUser(profileRes.data.user);
      setStats(profileRes.data.stats);
      setLevel(profileRes.data.level);
      setFollowStatus(followRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [api, userId, user?.id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleToggleFollow = async () => {
    setFollowLoading(true);
    try {
      const res = await api.post(`/user-follows/${userId}/toggle`);
      setFollowStatus({
        i_follow: res.data.following,
        they_follow: followStatus.they_follow,
        is_friend: res.data.is_friend,
      });
    } catch (err) {
      console.error(err);
    }
    setFollowLoading(false);
  };

  const handleSendMessage = async () => {
    try {
      const res = await api.post("/messages/conversations", { recipient_id: userId });
      navigate(`/messages/${res.data.conversation.id}`);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!profileUser) return (
    <div className="text-center py-16 text-muted-foreground">Utilisateur non trouve</div>
  );

  const isSelf = user?.id === userId;
  const socialLinks = profileUser.social_links || {};
  const hasSocialLinks = Object.values(socialLinks).some(Boolean);
  const joinDate = profileUser.created_at ? new Date(profileUser.created_at).toLocaleDateString("fr-FR", { month: "short", year: "numeric" }) : "";

  return (
    <div className="py-6 space-y-4" data-testid="user-profile-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl overflow-hidden shadow-md">
        {/* Cover */}
        <div className="h-36 bg-gradient-to-br from-[#FF6B35]/30 via-[#7000FF]/20 to-[#0F0A1F] relative">
          {profileUser.cover_url && (
            <img src={profileUser.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          <button onClick={() => navigate(-1)} className="absolute top-3 left-3 bg-black/40 backdrop-blur-sm text-white p-2 rounded-full">
            <ArrowLeft className="w-4 h-4" />
          </button>
        </div>

        <div className="bg-card border border-border px-5 pb-5 pt-14 relative">
          {/* Avatar - positioned to overlap banner */}
          <div className="absolute -top-10 left-5 z-10">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center border-4 border-card shadow-xl overflow-hidden">
              {profileUser.avatar_url ? (
                <img src={profileUser.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-heading font-bold text-3xl">
                  {(profileUser.pseudo || "?")[0].toUpperCase()}
                </span>
              )}
            </div>
          </div>

          {/* Name & Info */}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading font-bold text-xl text-card-foreground truncate" data-testid="user-profile-pseudo">
                {profileUser.pseudo}
              </h1>
              {level && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${level.color}20`, color: level.color }}>
                  <Zap className="w-2.5 h-2.5" /> Nv.{level.level}
                </span>
              )}
              {followStatus.is_friend && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-pink-100 text-pink-600" data-testid="friend-badge">
                  <Heart className="w-2.5 h-2.5" /> Ami
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="px-2 py-0.5 rounded-full bg-muted text-[10px] font-medium text-card-foreground/80 border border-border">
                {profileUser.role === "artist" ? "Artiste" : "Fan"}
              </span>
              {profileUser.city && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3" /> {profileUser.city}
                </span>
              )}
            </div>
          </div>

          {/* Action buttons */}
          {!isSelf && (
            <div className="flex gap-2 mt-4" data-testid="user-action-buttons">
              <button
                onClick={handleToggleFollow}
                disabled={followLoading}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full font-bold text-sm transition-all ${
                  followStatus.i_follow
                    ? "bg-muted text-card-foreground/80 border border-border"
                    : "bg-[#FF6B35] text-white shadow-lg shadow-[#FF6B35]/30"
                }`}
                data-testid="follow-user-btn"
              >
                {followStatus.i_follow ? (
                  <><UserCheck className="w-4 h-4" /> {followStatus.is_friend ? "Ami" : "Suivi"}</>
                ) : (
                  <><UserPlus className="w-4 h-4" /> Suivre</>
                )}
              </button>
              <button
                onClick={handleSendMessage}
                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm bg-[#7000FF] text-white shadow-lg shadow-[#7000FF]/30 hover:scale-105 active:scale-95 transition-transform"
                data-testid="message-user-btn"
              >
                <MessageCircle className="w-4 h-4" /> Message
              </button>
            </div>
          )}

          {profileUser.bio && (
            <p className="text-sm text-card-foreground/80 mt-3 leading-relaxed">{profileUser.bio}</p>
          )}

          {profileUser.genres?.length > 0 && (
            <div className="flex gap-1.5 mt-3 flex-wrap">
              {profileUser.genres.map((g) => (
                <span key={g} className="text-[10px] px-2.5 py-1 rounded-full bg-[#FF6B35]/8 text-[#FF6B35] border border-[#FF6B35]/20 font-medium">{g}</span>
              ))}
            </div>
          )}

          {hasSocialLinks && (
            <div className="flex gap-2 mt-3">
              {SOCIAL_PLATFORMS.map(({ key, icon: Icon, url: makeUrl }) => {
                const val = socialLinks[key];
                if (!val) return null;
                return (
                  <a key={key} href={makeUrl(val)} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all" data-testid={`user-social-${key}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </a>
                );
              })}
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            <div className="bg-white rounded-2xl p-2.5 text-center border border-border">
              <Users className="w-4 h-4 text-[#7000FF] mx-auto mb-0.5" />
              <p className="font-mono-credits font-bold text-base text-card-foreground">{stats.follows_count}</p>
              <p className="text-[9px] text-muted-foreground">Suivis</p>
            </div>
            <div className="bg-white rounded-2xl p-2.5 text-center border border-border">
              <Trophy className="w-4 h-4 text-[#FFD700] mx-auto mb-0.5" />
              <p className="font-mono-credits font-bold text-base text-card-foreground">{stats.badges_unlocked}</p>
              <p className="text-[9px] text-muted-foreground">Badges</p>
            </div>
            <div className="bg-white rounded-2xl p-2.5 text-center border border-border">
              <Target className="w-4 h-4 text-emerald-500 mx-auto mb-0.5" />
              <p className="font-mono-credits font-bold text-base text-card-foreground">{stats.challenges_completed}</p>
              <p className="text-[9px] text-muted-foreground">Defis</p>
            </div>
            <div className="bg-white rounded-2xl p-2.5 text-center border border-border">
              <Calendar className="w-4 h-4 text-[#FF8FA3] mx-auto mb-0.5" />
              <p className="text-[10px] font-medium text-card-foreground leading-tight">{joinDate}</p>
              <p className="text-[9px] text-muted-foreground">Membre</p>
            </div>
          </div>

          {/* Link to Artist profile if applicable */}
          {profileUser.artist_id && (
            <button
              onClick={() => navigate(`/artist/${profileUser.artist_id}`)}
              className="w-full mt-4 bg-gradient-to-r from-[#7000FF]/10 to-[#FF6B35]/10 border border-[#7000FF]/20 rounded-2xl p-3 flex items-center gap-3 hover:border-[#7000FF]/40 transition-all"
              data-testid="user-artist-profile-link"
            >
              <div className="w-9 h-9 rounded-lg bg-[#7000FF]/15 flex items-center justify-center">
                <Music className="w-4 h-4 text-[#7000FF]" />
              </div>
              <div className="text-left flex-1">
                <p className="font-heading font-bold text-xs text-card-foreground">Voir le profil artiste</p>
                <p className="text-[10px] text-muted-foreground">Decouvre ses publications</p>
              </div>
            </button>
          )}
        </div>
      </motion.div>

      {/* Fan Level */}
      <FanLevelBadge userId={userId} />

      {/* Badges */}
      <BadgesSection userId={userId} />
    </div>
  );
}
